/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {PackageItem} from "types/entities/PackageItem";
import {GroupedPackageItem} from "types/entities/GroupedPackageItem";
import {translate} from "utils/utility";
import {useTypedSelector} from "helpers/reducers";
import {CREDIT_GROUP_NAME, foodGroups, isFoodGroup} from 'utils/offers/addItemToCalculationUtils'

export {foodGroups, isFoodGroup}

export interface GroupSubNameItem {
    subGroupName: string | null
    items: GroupedPackageItem[]
}

export interface GroupNameItem {
    groupName: string
    subGroups: GroupSubNameItem[]
}
// grouped package items
export const useGetGroupedUpsells = (list: PackageItem[]) => {
    // create grouped upsells
    // User wants ALL items listed out separately, so no variant_id grouping.
    const groupedItems = list.map((item): GroupedPackageItem => {
        return {
            groupName: item.extra?.groupName,
            subGroupName: item.extra?.subGroupName || null,
            variant_id: null,
            items: [item],
            image: item.image,
            position: item.position,
            name: item.name,
            description: item.description,
            description_html: item.description_html,
			custom_text: item.custom_text,
            packageItemVariant: item.packageItemVariant,
            is_variant: item.is_variant,
            variant_description: translate(item.variant_description),
        }
    })

    // sort groups by their position
    const groupedUpsellsAll = groupedItems.sort((a: GroupedPackageItem, b: GroupedPackageItem) => {
        if (a.position === null && b.position !== null) return 1
        if (b.position === null && a.position !== null) return -1
        if (a.position === null && b.position === null) return 0
        return a.position! - b.position!
    })

    const mappedByGroupNames = groupedUpsellsAll
        .reduce(
            (acc: { [key: string]: { [subKey: string]: GroupedPackageItem[] } }, item) => {
                const groupKey = !item.groupName ? 'Eintritt' : item.groupName
                const subGroupKey = item.subGroupName || 'default'
                
                if (!acc[groupKey]) {
                    acc[groupKey] = {}
                }
                if (!acc[groupKey][subGroupKey]) {
                    acc[groupKey][subGroupKey] = []
                }
                acc[groupKey][subGroupKey].push(item)
                return acc
            },
            {} as { [key: string]: { [subKey: string]: GroupedPackageItem[] } }
        )

    const groupOrder = ['Eintritt', CREDIT_GROUP_NAME, 'Competitions', ...foodGroups]

    const sortedByGroupNames: GroupNameItem[] = Object.entries(mappedByGroupNames)
        .map(([groupName, subGroupsDict]) => {
            const subGroupNamesInOrder = [...new Set(groupedUpsellsAll
                .filter(item => (!item.groupName ? 'Eintritt' : item.groupName) === groupName)
                .map(item => item.subGroupName || 'default')
            )]

            return {
                groupName,
                subGroups: subGroupNamesInOrder.map(subGroupName => ({
                    subGroupName: subGroupName === 'default' ? null : subGroupName,
                    items: subGroupsDict[subGroupName] || [],
                })),
            }
        })
        .sort((a, b) => {
            const aIndex = groupOrder.indexOf(a.groupName)
            const bIndex = groupOrder.indexOf(b.groupName)

            if (aIndex === -1 && bIndex === -1) return 0
            if (aIndex === -1) return 1
            if (bIndex === -1) return -1

            return aIndex - bIndex
        })

    return {
        groupedUpsellsAll,
        sortedByGroupNames,
    }
}

export const useComputeInitialCount = (packageItem: PackageItem) => {
    const bookingFilter = useTypedSelector((state) => state.bookingState.bookingProcess.filter)

    const computeInitialCount = (maximumValue: number) => {
        const competitionUpsell = packageItem.extra?.groupName === 'Competitions'
        const isCredit =
            packageItem.extra?.groupName === CREDIT_GROUP_NAME || !!packageItem.creditPackage
        let totalCount = packageItem.minimum_persons_count === 0 ? 1 : packageItem.minimum_persons_count
        if (competitionUpsell) totalCount = 1
        if (isCredit) {
            totalCount = bookingFilter.adults
        } else if (isFoodGroup(packageItem.extra?.groupName)) {
            totalCount = bookingFilter.adults + bookingFilter.children
        }
        return Math.min(Math.max(totalCount, 1), maximumValue > 0 ? maximumValue : totalCount)
    }

    return {
        computeInitialCount,
    }
}
